import React, { useMemo } from 'react';
import styles from './Countdown.module.scss';
import clsx from 'clsx';

import { StylesCustomizer } from '../../molecules/stylesCustomizer';
import Button from '../button';
import {
  adjustForTimezone,
  COUNTDOWN_TIMEZONES,
  COUNTDOWN_TYPES,
  COUNTDOWN_VIEWS,
  getUserTimezoneFormat,
} from './utils/timezone';

import { Text } from '@srcDS/components/typography/text/Text';

import { useCountdown } from '@srcDS/components/atoms/countdown/hooks/useCountdown';
import { useCountdownStyles } from '@srcDS/components/atoms/countdown/hooks/useContdownStyles';
import { useTimeFormatter } from '@srcDS/components/atoms/countdown/hooks/useTimeFormatter';
import { CountdownProps, TimerTextProps } from '@srcDS/components/atoms/countdown/CountdownProps';

const TimerText = ({ styles: textStyles, text }: TimerTextProps) => (
  <Text className={styles.countStringText} as="p" variables={textStyles}>
    <span dangerouslySetInnerHTML={{ __html: text }} />
  </Text>
);

const Countdown = (props: CountdownProps) => {
  const {
    timezone = COUNTDOWN_TIMEZONES.aest,
    viewAs = COUNTDOWN_VIEWS.string,
    type = COUNTDOWN_TYPES.inner,
    countdownTo,
    background,
    text,
    buttons,
    textStyles,
    countTextStyles,
  } = props;

  const viewAsString = viewAs === COUNTDOWN_VIEWS.string;
  const viewAsIndependentBlock = viewAs === COUNTDOWN_VIEWS.block && type === COUNTDOWN_TYPES.independent;

  const StyledText = useMemo(() => StylesCustomizer(TimerText), []);

  const userTimezone = getUserTimezoneFormat();
  const timeValue = useMemo(
    () => adjustForTimezone(countdownTo, timezone, userTimezone),
    [countdownTo, timezone, userTimezone],
  );

  const timeLeft = useCountdown(timeValue);
  const { containerStyles, timerOrderStyle, buttonColorStyle } = useCountdownStyles(props as any);
  const { formatTime, textFormat } = useTimeFormatter(viewAsString, countTextStyles);

  return (
    <div
      className={clsx(styles.container, {
        [styles.hasBackground]: background,
        [styles.stringContainer]: viewAsString,
        [styles.independentBlockContainer]: viewAsIndependentBlock,
      })}
      style={containerStyles as React.CSSProperties}
    >
      {text && <StyledText styles={textStyles} text={text} children={undefined} />}

      <div
        className={clsx(styles.countdownWrap, {
          [styles.withDays]: timeLeft.days > 0,
          [styles.asString]: viewAsString,
        })}
        style={timerOrderStyle as React.CSSProperties}
      >
        {timeLeft.days > 0 && <span className={styles.countBlock}>{formatTime(timeLeft.days, textFormat.days)}</span>}
        <span className={styles.countBlock}>{formatTime(timeLeft.hours, textFormat.hours)}</span>
        <span className={styles.countBlock}>{formatTime(timeLeft.minutes, textFormat.minutes)}</span>
        <span className={styles.countBlock}>{formatTime(timeLeft.seconds, textFormat.seconds)}</span>
      </div>

      {buttons?.length > 0 && (
        <div
          className={styles.buttonsWrap}
          style={
            {
              ['--button-count' as string]: buttons?.length,
              ...buttonColorStyle,
            } as React.CSSProperties
          }
        >
          {buttons.map((btn, index) => (
            /*@ts-ignore*/
            <Button
              key={`button-${index}`}
              nodeLink={btn.buttonUrl}
              className={styles.button}
              variant="text"
              size="medium"
            >
              {btn.buttonTitle}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
};

export default Countdown;
